import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import {
    Database, RotateCcw, Clock, Zap, AlertTriangle, Star,
    XCircle, FastForward, Trash2, Play, Activity, CheckCircle2,
    Copy, Terminal,
} from 'lucide-react';

const MANDATE_IDS = Array.from({ length: 11 }, (_, i) => `qa-mandate-${String(i + 1).padStart(3, '0')}`);
const STATUSES = ['draft', 'generated', 'sent', 'acknowledged', 'active', 'sold', 'denounced', 'closed'];
const DAYS_OPTS = [1, 3, 7, 10, 14, 21, 30];

const CARD = "rounded-2xl bg-card border border-white/10 p-4 sm:p-5";
const TITLE = "text-xs font-bold uppercase tracking-wider text-[#FF914D] mb-3 flex items-center gap-2";
const SUB = "text-[11px] text-muted-foreground mb-3";
const INPUT = "w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-sm focus:border-[#FF914D]/50 focus:outline-none";

export default function QAPanel() {
    const { user } = useAuth();
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [log, setLog] = useState([]);
    const [credentials, setCredentials] = useState(null);

    // States des sélecteurs
    const [ttTarget, setTtTarget] = useState('mandate');
    const [ttId, setTtId] = useState(MANDATE_IDS[0]);
    const [ttDays, setTtDays] = useState(10);
    const [inactId, setInactId] = useState(MANDATE_IDS[3]);
    const [inactDays, setInactDays] = useState(14);
    const [ratingId, setRatingId] = useState(MANDATE_IDS[6]);
    const [ratingScore, setRatingScore] = useState(5);
    const [transId, setTransId] = useState(MANDATE_IDS[0]);
    const [transStatus, setTransStatus] = useState('sent');
    const [denId, setDenId] = useState(MANDATE_IDS[3]);
    const [cronJobs, setCronJobs] = useState([]);

    const appendLog = useCallback((kind, text, extra = null) => {
        const ts = new Date().toLocaleTimeString('fr-FR');
        setLog(prev => [{ ts, kind, text, extra }, ...prev].slice(0, 50));
    }, []);

    const loadStatus = useCallback(async () => {
        try {
            const res = await api.get('/qa/status');
            setStatus(res.data);
        } catch (e) {
            appendLog('error', `Status: ${e.response?.data?.detail || e.message}`);
        }
    }, [appendLog]);

    const loadCronJobs = useCallback(async () => {
        try {
            const res = await api.get('/qa/cron-jobs');
            setCronJobs(res.data.jobs || []);
        } catch {
            // silent
        }
    }, []);

    useEffect(() => {
        loadStatus();
        loadCronJobs();
    }, [loadStatus, loadCronJobs]);

    // Restrict to super_admin only
    if (user && user.role !== 'super_admin') {
        return (
            <div className="p-8 text-center" data-testid="qa-restricted">
                <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <p className="text-foreground font-bold mb-1">Accès réservé au Super-Admin</p>
                <p className="text-xs text-muted-foreground">Le panneau QA est strictement réservé.</p>
            </div>
        );
    }

    const callApi = async (label, method, url, body = null) => {
        setBusy(true);
        try {
            const res = method === 'post'
                ? await api.post(url, body || {})
                : method === 'delete'
                    ? await api.delete(url)
                    : await api.get(url);
            appendLog('ok', `${label} → OK`, res.data);
            return res.data;
        } catch (e) {
            const msg = e.response?.data?.detail || e.message;
            appendLog('error', `${label} → ${typeof msg === 'string' ? msg : JSON.stringify(msg)}`);
            toast.error(`${label}: échec`);
            return null;
        } finally {
            setBusy(false);
            loadStatus();
        }
    };

    const handleSeed = async () => {
        const r = await callApi('Seed scénario complet', 'post', '/qa/seed-scenario');
        if (r?.credentials) {
            setCredentials(r.credentials);
            toast.success('Scénario QA seeded — 11 mandats créés');
        }
    };

    const handleWipe = async () => {
        if (!window.confirm('Confirmer la suppression de TOUTES les données QA ?')) return;
        const r = await callApi('Wipe QA', 'delete', '/qa/wipe');
        if (r) toast.success('Données QA effacées');
    };

    const copyCreds = () => {
        if (!credentials) return;
        const lines = Object.entries(credentials).map(([k, v]) => `${k}: ${v.email} / ${v.password}`).join('\n');
        navigator.clipboard.writeText(lines);
        toast.success('Identifiants copiés');
    };

    return (
        <div className="space-y-5 max-w-7xl mx-auto pb-12" data-testid="qa-panel-page">
            {/* Header */}
            <div>
                <h1 className="text-2xl sm:text-3xl font-black text-foreground flex items-center gap-3">
                    <Terminal className="w-6 h-6 text-[#FF914D]" />
                    Mode QA / Test
                </h1>
                <p className="text-xs text-muted-foreground mt-1">
                    Panneau réservé Super-Admin · Manipulation directe de la base pour tests E2E
                </p>
            </div>

            {/* Status snapshot */}
            <div className={CARD}>
                <div className="flex items-center justify-between mb-2">
                    <p className={TITLE}>
                        <Activity className="w-3.5 h-3.5" /> Snapshot QA en cours
                    </p>
                    <button
                        onClick={loadStatus}
                        className="text-[10px] px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="qa-refresh-status"
                    >
                        <RotateCcw className="w-3 h-3 inline mr-1" /> Refresh
                    </button>
                </div>
                {status ? (
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                        {Object.entries(status.counts || {}).map(([k, v]) => (
                            <div key={k} className="rounded-lg bg-white/[0.02] border border-white/5 p-2.5">
                                <p className="text-[10px] uppercase text-muted-foreground">{k}</p>
                                <p className="text-lg font-black text-foreground">{v}</p>
                            </div>
                        ))}
                    </div>
                ) : <p className="text-xs text-muted-foreground">Chargement…</p>}
                {status?.mandates_by_status && Object.keys(status.mandates_by_status).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                        {Object.entries(status.mandates_by_status).map(([k, v]) => (
                            <span key={k} className="px-2 py-0.5 rounded-full bg-[#FF914D]/10 border border-[#FF914D]/20 text-[#FF914D] text-[10px] font-bold">
                                {k}: {v}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Seed + Wipe */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className={CARD}>
                    <p className={TITLE}><Database className="w-3.5 h-3.5" /> 1. Seed scénario complet</p>
                    <p className={SUB}>
                        Crée 1 agence + 1 admin + 2 agents + 3 vendeurs + 11 mandats (tous statuts + multi-vendeurs + inactivité).
                        Tag <code className="text-[#FF914D]">qa_seed=True</code> pour purge facile.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            onClick={handleSeed}
                            disabled={busy}
                            className="bg-gradient-to-r from-[#681793] to-[#FF914D]"
                            data-testid="qa-seed-btn"
                        >
                            <Play className="w-4 h-4 mr-2" /> Générer
                        </Button>
                        <Button
                            onClick={handleWipe}
                            disabled={busy}
                            variant="outline"
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            data-testid="qa-wipe-btn"
                        >
                            <Trash2 className="w-4 h-4 mr-2" /> Wipe
                        </Button>
                    </div>
                    {credentials && (
                        <div className="mt-3 rounded-lg bg-white/[0.02] border border-[#FF914D]/20 p-3">
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-[10px] uppercase font-bold text-[#FF914D]">Identifiants QA</p>
                                <button onClick={copyCreds} className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="qa-copy-creds">
                                    <Copy className="w-3 h-3" /> Copier
                                </button>
                            </div>
                            <div className="space-y-0.5 font-mono text-[10px]">
                                {Object.entries(credentials).map(([role, c]) => (
                                    <p key={role}><span className="text-[#FF914D]">{role}:</span> {c.email} / {c.password}</p>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Time-travel */}
                <div className={CARD}>
                    <p className={TITLE}><FastForward className="w-3.5 h-3.5" /> 2. Time-travel (backdate)</p>
                    <p className={SUB}>Recule de N jours les dates created_at / updated_at / last_activity_at</p>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                        <select className={INPUT} value={ttTarget} onChange={e => setTtTarget(e.target.value)} data-testid="qa-tt-target">
                            <option value="mandate">Mandat</option>
                            <option value="estimation">Estimation</option>
                            <option value="user">Utilisateur</option>
                            <option value="agency">Agence</option>
                            <option value="all_qa">Tous docs QA</option>
                        </select>
                        <select className={INPUT} value={ttDays} onChange={e => setTtDays(Number(e.target.value))} data-testid="qa-tt-days">
                            {DAYS_OPTS.map(d => <option key={d} value={d}>−{d} jours</option>)}
                        </select>
                    </div>
                    {ttTarget !== 'all_qa' && (
                        <select className={INPUT + ' mb-2'} value={ttId} onChange={e => setTtId(e.target.value)} data-testid="qa-tt-id">
                            {MANDATE_IDS.map(mid => <option key={mid} value={mid}>{mid}</option>)}
                        </select>
                    )}
                    <Button
                        onClick={() => callApi(`Time-travel ${ttTarget} −${ttDays}j`, 'post', '/qa/time-travel', {
                            target: ttTarget,
                            target_id: ttTarget === 'all_qa' ? null : ttId,
                            days: ttDays,
                        })}
                        disabled={busy}
                        className="bg-white/10 hover:bg-white/15 w-full"
                        data-testid="qa-tt-btn"
                    >
                        <Clock className="w-4 h-4 mr-2" /> Reculer de {ttDays}j
                    </Button>
                </div>
            </div>

            {/* Simulateurs */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Inactivité */}
                <div className={CARD}>
                    <p className={TITLE}><AlertTriangle className="w-3.5 h-3.5" /> 3. Simuler inactivité</p>
                    <p className={SUB}>Backdate last_activity_at + génère notif "mandat inactif"</p>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                        <select className={INPUT} value={inactId} onChange={e => setInactId(e.target.value)} data-testid="qa-inact-id">
                            {MANDATE_IDS.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <select className={INPUT} value={inactDays} onChange={e => setInactDays(Number(e.target.value))} data-testid="qa-inact-days">
                            {DAYS_OPTS.map(d => <option key={d} value={d}>{d} jours</option>)}
                        </select>
                    </div>
                    <Button
                        onClick={() => callApi(`Inactivité ${inactDays}j sur ${inactId}`, 'post', '/qa/simulate-inactivity', { mandate_id: inactId, days: inactDays })}
                        disabled={busy}
                        className="bg-white/10 hover:bg-white/15 w-full"
                        data-testid="qa-inact-btn"
                    >
                        <AlertTriangle className="w-4 h-4 mr-2" /> Simuler
                    </Button>
                </div>

                {/* Rating */}
                <div className={CARD}>
                    <p className={TITLE}><Star className="w-3.5 h-3.5" /> 4. Simuler notation vendeur</p>
                    <p className={SUB}>Crée une notation + notification pour l'agent</p>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                        <select className={INPUT} value={ratingId} onChange={e => setRatingId(e.target.value)} data-testid="qa-rating-id">
                            {MANDATE_IDS.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <select className={INPUT} value={ratingScore} onChange={e => setRatingScore(Number(e.target.value))} data-testid="qa-rating-score">
                            {[1, 2, 3, 4, 5].map(s => <option key={s} value={s}>{`${s} etoiles`}</option>)}
                        </select>
                    </div>
                    <Button
                        onClick={() => callApi(`Notation ${ratingScore}★ sur ${ratingId}`, 'post', '/qa/simulate-rating', { mandate_id: ratingId, overall_score: ratingScore })}
                        disabled={busy}
                        className="bg-white/10 hover:bg-white/15 w-full"
                        data-testid="qa-rating-btn"
                    >
                        <Star className="w-4 h-4 mr-2" /> Noter
                    </Button>
                </div>

                {/* Transition */}
                <div className={CARD}>
                    <p className={TITLE}><Zap className="w-3.5 h-3.5" /> 5. Forcer transition statut</p>
                    <p className={SUB}>Bypass les contrôles métier — usage QA uniquement</p>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                        <select className={INPUT} value={transId} onChange={e => setTransId(e.target.value)} data-testid="qa-trans-id">
                            {MANDATE_IDS.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <select className={INPUT} value={transStatus} onChange={e => setTransStatus(e.target.value)} data-testid="qa-trans-status">
                            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <Button
                        onClick={() => callApi(`Transition ${transId} → ${transStatus}`, 'post', '/qa/transition', { mandate_id: transId, target_status: transStatus })}
                        disabled={busy}
                        className="bg-white/10 hover:bg-white/15 w-full"
                        data-testid="qa-trans-btn"
                    >
                        <Zap className="w-4 h-4 mr-2" /> Forcer
                    </Button>
                </div>

                {/* Dénonciation */}
                <div className={CARD}>
                    <p className={TITLE}><XCircle className="w-3.5 h-3.5" /> 6. Simuler dénonciation</p>
                    <p className={SUB}>Marque le mandat comme dénoncé + notif urgente</p>
                    <select className={INPUT + ' mb-2'} value={denId} onChange={e => setDenId(e.target.value)} data-testid="qa-den-id">
                        {MANDATE_IDS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <Button
                        onClick={() => callApi(`Dénonciation ${denId}`, 'post', '/qa/simulate-denunciation', { mandate_id: denId, reason: 'Test QA' })}
                        disabled={busy}
                        className="bg-white/10 hover:bg-white/15 w-full"
                        data-testid="qa-den-btn"
                    >
                        <XCircle className="w-4 h-4 mr-2" /> Dénoncer
                    </Button>
                </div>
            </div>

            {/* Cron jobs */}
            <div className={CARD}>
                <p className={TITLE}><Zap className="w-3.5 h-3.5" /> 7. Cron jobs (APScheduler) — exécution immédiate</p>
                <p className={SUB}>Déclenche le job NOW au lieu d'attendre son trigger naturel</p>
                {cronJobs.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">Chargement des jobs…</p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {cronJobs.map(j => (
                            <button
                                key={j.id}
                                onClick={() => callApi(`Cron ${j.id}`, 'post', '/qa/trigger-cron', { job_id: j.id })}
                                disabled={busy}
                                className="text-left rounded-lg bg-white/[0.02] border border-white/5 hover:border-[#FF914D]/30 hover:bg-[#FF914D]/5 p-3 transition-colors disabled:opacity-50"
                                data-testid={`qa-cron-${j.id}`}
                            >
                                <p className="text-xs font-bold text-foreground">{j.name}</p>
                                <p className="text-[9px] text-muted-foreground font-mono">{j.id}</p>
                                {j.next_run_time && <p className="text-[9px] text-muted-foreground mt-0.5">Next: {j.next_run_time}</p>}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Log */}
            <div className={CARD}>
                <p className={TITLE}><Activity className="w-3.5 h-3.5" /> Log d'actions ({log.length})</p>
                {log.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">Aucune action effectuée pour le moment.</p>
                ) : (
                    <div className="space-y-1 max-h-80 overflow-y-auto font-mono">
                        {log.map((l, i) => (
                            <div key={i} className={`text-[10px] flex items-start gap-2 px-2 py-1 rounded ${l.kind === 'ok' ? 'bg-green-500/5 text-green-400' : 'bg-red-500/5 text-red-400'}`}>
                                {l.kind === 'ok' ? <CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" /> : <XCircle className="w-3 h-3 mt-0.5 shrink-0" />}
                                <span className="text-muted-foreground">{l.ts}</span>
                                <span className="text-foreground flex-1 break-all">{l.text}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
